import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Loader2, LogOut, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import Logo from '@/components/Logo';
import StatsCards from '@/components/admin/StatsCards';
import ApplicationsTable from '@/components/admin/ApplicationsTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function AdminPage() {
  const [checking, setChecking] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const loadData = async () => {
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/admin/applications`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setApps(a.data.applications);
      setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) setAdmin(null);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/admin/me`, { withCredentials: true })
      .then(({ data }) => setAdmin(data))
      .catch(() => setAdmin(null))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (admin) loadData();
  }, [admin]);

  const login = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError('');
    try {
      const { data } = await axios.post(`${API}/admin/login`, creds, { withCredentials: true });
      setAdmin(data);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoginBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/admin/logout`, {}, { withCredentials: true }).catch(() => {});
    setAdmin(null);
    setApps([]);
    setStats(null);
  };

  const patch = async (referenceId, body) => {
    const { data } = await axios.patch(`${API}/admin/applications/${referenceId}`, body, { withCredentials: true });
    setApps((list) => list.map((x) => (x.reference_id === referenceId ? { ...x, ...data } : x)));
    loadData();
    return data;
  };

  const filtered = apps.filter(
    (a) =>
      !search ||
      a.reference_id.toLowerCase().includes(search.toLowerCase()) ||
      (a.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (a.phone || '').includes(search)
  );

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/admin/applications/export`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payday-india-applications-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const inputCls =
    'w-full rounded-lg border border-white/10 bg-ink/70 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-pulse/70 focus:ring-2 focus:ring-pulse/20';

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="admin-checking">
        <Loader2 size={28} className="animate-spin text-pulse" />
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5 pt-24 pb-16" data-testid="admin-login-view">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl border border-white/10 bg-panel/90 p-8 shadow-2xl backdrop-blur-xl sm:p-10">
          <div className="flex justify-center"><Logo /></div>
          <h1 className="mt-6 text-center font-display text-2xl font-extrabold text-white">Admin sign in</h1>
          <p className="mt-2 text-center text-sm text-slate-500">Restricted to PayDay India staff.</p>
          <form onSubmit={login} className="mt-8 space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              placeholder="admin@paydayindia.com"
              value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              className={inputCls}
              aria-label="Email"
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              placeholder="Password"
              value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
              className={inputCls}
              aria-label="Password"
            />
            {loginError && (
              <p data-testid="admin-login-error" className="text-xs font-medium text-red-400">{loginError}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loginBusy}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-pulse py-3 font-display font-bold text-ink transition-colors hover:bg-pulse-dim disabled:opacity-60"
            >
              {loginBusy ? <Loader2 size={17} className="animate-spin" /> : <ShieldCheck size={17} />}
              Sign in
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-5 pb-20 pt-28 sm:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-white">Loan applications</h1>
          <p className="mt-1 text-sm text-slate-500">Signed in as {admin.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-export-csv-button"
            onClick={exportCsv}
            disabled={exporting}
            className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:border-pulse/50 hover:text-pulse disabled:opacity-60"
          >
            <Download size={15} /> {exporting ? 'Preparing…' : 'Export CSV'}
          </button>
          <button
            data-testid="admin-refresh-button"
            onClick={loadData}
            className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:border-pulse/50 hover:text-pulse"
          >
            <RefreshCw size={15} /> Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-red-500/15 hover:text-red-300"
          >
            <LogOut size={15} /> Log out
          </button>
        </div>
      </div>

      {stats && <StatsCards stats={stats} />}

      <div className="mt-8 flex items-center gap-3 rounded-xl border border-white/10 bg-panel/60 px-4 py-3" data-testid="admin-search">
        <Search size={16} className="text-slate-500" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by reference ID, name or phone…"
          className="w-full bg-transparent text-sm text-white outline-none placeholder:text-slate-600"
          aria-label="Search applications"
        />
      </div>

      <ApplicationsTable apps={filtered} onPatch={patch} />
    </div>
  );
}
